import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';

import { requestOtp, verifyOtp } from '../api/auth.api';
import { loginSchema, otpSchema, type LoginFormValues, type OtpFormValues } from '../schemas/login.schema';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { messages } from '@/shared/constants/messages';
import { toUserMessage } from '@/shared/lib/errors';

const copy = messages.auth;

/**
 * Dois passos: e-mail → código de 6 dígitos. Depois do passo 1, a tela
 * mostra a MESMA frase (`messages.auth.otpSent`) esteja o e-mail cadastrado
 * ou não — nunca ramifique esse texto por resultado (regra 6 / ADR-002 /
 * US-02). Reenviar código é a mesma chamada de `requestOtp`.
 *
 * **O título da coluna mora aqui, e não na página.** Ele muda com o passo
 * ("Entrar no painel" → "Digite o código"), e o passo é o `sentTo` deste
 * componente. Subir o estado para a `LoginPage` só para ela poder escrever o
 * `<h1>` partiria o formulário em dois donos — o preço de manter um `<h1>` na
 * página seria a chance de um passo trocar sem o título trocar junto.
 */
export function OtpLoginForm() {
  const navigate = useNavigate();
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isResending, setIsResending] = useState(false);

  const emailForm = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '' },
  });

  const otpForm = useForm<OtpFormValues>({
    resolver: zodResolver(otpSchema),
    defaultValues: { token: '' },
  });

  async function onRequestOtp(values: LoginFormValues) {
    setSubmitError(null);
    try {
      await requestOtp(values.email);
      setSentTo(values.email);
    } catch (error) {
      setSubmitError(toUserMessage(error));
    }
  }

  async function onVerifyOtp(values: OtpFormValues) {
    if (!sentTo) return;
    setSubmitError(null);
    try {
      await verifyOtp(sentTo, values.token);
      navigate('/', { replace: true });
    } catch (error) {
      setSubmitError(toUserMessage(error));
    }
  }

  async function onResend() {
    if (!sentTo) return;
    setSubmitError(null);
    setIsResending(true);
    try {
      await requestOtp(sentTo);
    } catch (error) {
      setSubmitError(toUserMessage(error));
    } finally {
      setIsResending(false);
    }
  }

  // Os dois passos são `<form>` diferentes: trocar um pelo outro já remonta o
  // nó, e a animação de entrada roda sozinha. É o que faz o código de 6 dígitos
  // PARECER ter substituído o e-mail — e o mesmo vale na volta, ao clicar em
  // "usar outro e-mail".
  if (sentTo) {
    return (
      <form
        onSubmit={otpForm.handleSubmit(onVerifyOtp)}
        noValidate
        className="animate-rise-in space-y-6"
      >
        <div className="space-y-2">
          <h1 className="text-2xl font-semibold tracking-tight">{copy.otpTitle}</h1>
          <div role="status" aria-live="polite" className="space-y-1">
            <p className="text-sm text-muted-foreground">{copy.otpSent}</p>
          </div>
        </div>

        <div className="space-y-1.5">
          <label htmlFor="token" className="text-sm font-medium">
            {copy.otpLabel}
          </label>
          {/* Seis dígitos merecem um campo que PAREÇA de seis dígitos: alto,
              centralizado e com espaçamento entre caracteres. `tabular` porque
              sem largura fixa por dígito o código dança enquanto é digitado —
              num campo com `tracking` largo isso é impossível de ignorar.
              O recuo à esquerda compensa o `letter-spacing`, que sobra depois
              do último caractere e desloca o texto centralizado. */}
          <Input
            id="token"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            maxLength={6}
            placeholder="000000"
            className="tabular h-14 pl-[0.5em] text-center text-2xl font-medium tracking-[0.5em] placeholder:tracking-[0.5em] placeholder:text-muted-foreground/40"
            aria-invalid={otpForm.formState.errors.token ? 'true' : undefined}
            aria-describedby={
              otpForm.formState.errors.token ? 'token-error token-hint' : 'token-hint'
            }
            {...otpForm.register('token')}
          />
          <p id="token-hint" className="text-xs text-muted-foreground">
            {copy.otpHint}
          </p>
          {otpForm.formState.errors.token ? (
            <p id="token-error" className="animate-reveal text-sm text-destructive">
              {otpForm.formState.errors.token.message}
            </p>
          ) : null}
        </div>

        {submitError ? (
          <p role="alert" className="animate-reveal text-sm text-destructive">
            {submitError}
          </p>
        ) : null}

        <div className="space-y-4">
          <Button
            type="submit"
            size="lg"
            className="w-full"
            disabled={otpForm.formState.isSubmitting}
          >
            {otpForm.formState.isSubmitting ? copy.otpSubmitting : copy.otpSubmit}
          </Button>

          {/* O e-mail digitado, de volta na tela: quem tem dois endereços não
              precisa lembrar em qual caixa procurar. Não vaza nada — é o que a
              própria pessoa acabou de escrever, e o texto acima segue dizendo
              "se este e-mail estiver cadastrado". */}
          <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 text-sm">
            <span className="min-w-0 text-muted-foreground">
              {copy.otpEmailLabel}:{' '}
              <span className="break-all font-medium text-foreground">{sentTo}</span>
            </span>
            <Button
              type="button"
              variant="link"
              size="sm"
              className="h-auto shrink-0 p-0"
              disabled={isResending}
              onClick={() => void onResend()}
            >
              {isResending ? copy.resending : copy.resend}
            </Button>
          </div>

          <Button
            type="button"
            variant="link"
            size="sm"
            className="h-auto p-0 text-muted-foreground"
            onClick={() => {
              setSentTo(null);
              setSubmitError(null);
              otpForm.reset();
            }}
          >
            {copy.changeEmail}
          </Button>
        </div>
      </form>
    );
  }

  return (
    <form
      onSubmit={emailForm.handleSubmit(onRequestOtp)}
      noValidate
      className="animate-rise-in space-y-6"
    >
      <div className="space-y-2">
        {/* A marca NÃO se repete aqui. Ela já está no painel laranja, que em
            qualquer largura fica acima ou ao lado deste formulário — no celular
            como faixa, no desktop como coluna. */}
        <h1 className="text-2xl font-semibold tracking-tight">{copy.title}</h1>
        <p className="text-sm leading-relaxed text-muted-foreground">{copy.subtitle}</p>
      </div>

      <div className="space-y-1.5">
        <label htmlFor="email" className="text-sm font-medium">
          {copy.emailLabel}
        </label>
        <Input
          id="email"
          type="email"
          autoComplete="email"
          autoFocus
          placeholder={copy.emailPlaceholder}
          className="h-11"
          aria-invalid={emailForm.formState.errors.email ? 'true' : undefined}
          aria-describedby={emailForm.formState.errors.email ? 'email-error' : undefined}
          {...emailForm.register('email')}
        />
        {emailForm.formState.errors.email ? (
          <p id="email-error" className="animate-reveal text-sm text-destructive">
            {emailForm.formState.errors.email.message}
          </p>
        ) : null}
      </div>

      {submitError ? (
        <p role="alert" className="animate-reveal text-sm text-destructive">
          {submitError}
        </p>
      ) : null}

      <div className="space-y-3">
        <Button
          type="submit"
          size="lg"
          className="w-full"
          disabled={emailForm.formState.isSubmitting}
        >
          {emailForm.formState.isSubmitting ? copy.emailSubmitting : copy.emailSubmit}
        </Button>
        {/* `text-balance` porque a frase quebra em duas linhas em quase toda
            largura, e centralizada com uma palavra órfã na segunda ela parece
            texto cortado — logo abaixo do botão, isso lê como erro. */}
        <p className="text-balance text-center text-xs text-muted-foreground">
          {copy.noPassword}
        </p>
      </div>
    </form>
  );
}
